import React, { useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { INTERVIEWS_CHANGED, interviewsChanged, schedulingRequest } from "../utils/interviewScheduling";
import { RECRUITING_STAGES, pipelineStageKey } from "../utils/pipelineStages";
import CandidateScheduleDialog from "./CandidateScheduleDialog";

export default function InterviewFollowUps({ candidateId, compact = false, onCountChange, onResolved, onOpenCandidate }) {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [action, setAction] = useState("advance");
  const [stage, setStage] = useState("");
  const [busy, setBusy] = useState(false);
  const [editor, setEditor] = useState(null);
  useEffect(() => { onCountChange?.(records.length); }, [records.length, onCountChange]);
  useEffect(() => {
    let active = true, requestId = 0;
    const load = async () => {
      const current = ++requestId;
      try {
        const result = await schedulingRequest(`/calendar/interview-follow-ups${candidateId ? `?candidateId=${encodeURIComponent(candidateId)}` : ""}`);
        if (active && current === requestId) { setRecords(result.interviews || []); setError(""); }
      } catch (err) { if (active && current === requestId) setError(err.message); }
    };
    load();
    const timer = setInterval(load, 60000);
    window.addEventListener(INTERVIEWS_CHANGED, load);
    return () => { active = false; clearInterval(timer); window.removeEventListener(INTERVIEWS_CHANGED, load); };
  }, [candidateId]);
  const stages = RECRUITING_STAGES.filter((item, index) => item.id !== "rejected" && index > RECRUITING_STAGES.findIndex(current => current.id === pipelineStageKey(selected?.current_stage || selected?.stage || "Interview")));
  async function save() {
    setBusy(true); setError("");
    try {
      if (action === "reschedule") {
        const { meeting } = await schedulingRequest(`/calendar/meetings/${selected.id}`);
        setEditor({ meeting, notes });
        setSelected(null);
      } else {
        await schedulingRequest(`/calendar/interviews/${selected.id}/outcome`, { method: "POST", body: JSON.stringify({ action, notes, stage }) });
        setSelected(null); interviewsChanged(); onResolved?.();
      }
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  if (!records.length && !error && !selected && !editor) return null;
  return <Box sx={{ my: 1, p: compact ? 0 : 2, border: compact ? 0 : "1px solid #FEDF89", borderRadius: 1 }}>
    {!!records.length && <>
      <Typography fontWeight={600} fontSize={compact ? 12.5 : 15}>{compact ? "" : "Needs Attention · "}{records.length} interview{records.length === 1 ? " needs" : "s need"} an outcome</Typography>
      {!compact && <Typography variant="body2" color="text.secondary">The scheduled interview has ended without a recorded outcome or stage progression. Record what happened and choose the next action.</Typography>}
      <Stack spacing={1} sx={{ mt: 1, maxHeight: compact ? 200 : 400, overflowY: "auto" }}>
        {records.map(record => <Box key={record.id}>
          <Link component={RouterLink} to={`/candidates?candidateId=${record.candidate_id}`} onClick={onOpenCandidate} sx={{ fontSize: compact ? 12 : 14 }}>{record.candidate_name}</Link>
          <Typography variant="caption" display="block">{record.job_title ? `${record.job_title} · ` : ""}{new Date(record.ends_at || record.starts_at).toLocaleString()}</Typography>
          <Button size="small" onClick={() => { setSelected(record); setNotes(""); setAction("advance"); setStage(""); setError(""); }}>Record outcome</Button>
        </Box>)}
      </Stack>
    </>}
    {error && !selected && <Alert severity="error">{error}</Alert>}
    <Dialog open={!!selected} onClose={busy ? undefined : () => setSelected(null)} fullWidth maxWidth="sm">
      <DialogTitle data-popup-header>Interview outcome · {selected?.candidate_name}</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <TextField label="What happened?" multiline minRows={3} value={notes} onChange={event => setNotes(event.target.value)} required disabled={busy} inputProps={{ maxLength: 10000 }} />
        <TextField select label="Next action" value={action} onChange={event => setAction(event.target.value)} disabled={busy}>
          <MenuItem value="reject">Reject</MenuItem>
          <MenuItem value="reschedule" disabled={!selected?.can_manage}>Reschedule</MenuItem>
          <MenuItem value="advance">Advance to the next stage</MenuItem>
        </TextField>
        {!selected?.can_manage && <Typography variant="caption">The calendar organizer can reschedule this interview.</Typography>}
        {action === "advance" && <TextField select label="Next stage" value={stage} onChange={event => setStage(event.target.value)} required disabled={busy}>
          {stages.map(item => <MenuItem key={item.id} value={item.label}>{item.label}</MenuItem>)}
        </TextField>}
      </Stack></DialogContent>
      <DialogActions data-popup-footer><Button disabled={busy} onClick={() => setSelected(null)}>Cancel</Button>
        <Button disabled={busy || !notes.trim() || (action === "advance" && !stage)} onClick={save}>{busy ? "Saving…" : action === "reschedule" ? "Choose new time" : "Save outcome"}</Button>
      </DialogActions>
    </Dialog>
    {editor && <CandidateScheduleDialog open mode="reschedule" initialEvent={editor.meeting} context={{ outcomeNotes: editor.notes }}
      onClose={() => setEditor(null)} onSaved={() => { setEditor(null); interviewsChanged(); onResolved?.(); }} />}
  </Box>;
}
